<template>
  <nav
    :aria-label="label"
    class="flex flex-col items-center justify-between gap-4"
  >
    <!-- Results summary -->

    <!-- Pager -->
    <div
      class="inline-flex items-center gap-1 rounded-xl bg-white p-1 shadow-sm ring-1 ring-gray-200"
    >
      <!-- First -->
      <button
        @click="$emit('first')"
        :disabled="isFirstPage"
        class="inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium ring-1 ring-inset bg-spicy-mustard-50 ring-mocha-mousse-300 hover:bg-spicy-mustard-200 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="First page"
        title="First page"
      >
        <Icon icon="mdi:chevron-double-left" width="16" />
      </button>

      <!-- Previous -->
      <button
        @click="$emit('previous')"
        :disabled="isFirstPage"
        class="inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium ring-1 ring-inset bg-spicy-mustard-50 ring-mocha-mousse-300 hover:bg-spicy-mustard-200 focus:outline-none disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Previous page"
        title="Previous page"
      >
        <Icon icon="mdi:navigate-before" width="16" />
      </button>

      <!-- Page numbers -->
      <template v-for="(page, idx) in paginationRange" :key="`${page}-${idx}`">
        <span
          v-if="page === DOTS"
          class="inline-flex h-9 w-9 items-center justify-center text-sm text-gray-400 select-none"
          aria-hidden="true"
        >
          …
        </span>
        <button
          v-else
          @click="$emit('page', page)"
          :aria-current="page === currentPage ? 'page' : undefined"
          :class="[
            'inline-flex h-9 min-w-9 items-center justify-center rounded-md px-3 text-sm font-medium ring-1 ring-inset transition focus:outline-none',
            page === currentPage
              ? 'bg-spicy-mustard-600 ring-mocha-mousse-600 hover:bg-spicy-mustard-600/90'
              : 'bg-spicy-mustard-50 ring-mocha-mousse-300 hover:bg-spicy-mustard-200',
          ]"
        >
          {{ page }}
        </button>
      </template>

      <!-- Next -->
      <button
        @click="$emit('next')"
        :disabled="isLastPage"
        class="inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium ring-1 ring-inset bg-spicy-mustard-50 ring-mocha-mousse-300 hover:bg-spicy-mustard-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Next page"
        title="Next page"
      >
        <Icon icon="mdi:navigate-next" width="16" />
      </button>

      <!-- Last -->
      <button
        @click="$emit('last')"
        :disabled="isLastPage"
        class="inline-flex h-9 w-9 items-center justify-center rounded-md text-sm font-medium ring-1 ring-inset bg-spicy-mustard-50 ring-mocha-mousse-300 hover:bg-spicy-mustard-200 focus:outline-none focus-visible:ring-2 focus-visible:ring-blue-500 disabled:opacity-40 disabled:cursor-not-allowed"
        aria-label="Last page"
        title="Last page"
      >
        <Icon icon="mdi:chevron-double-right" width="16" />
      </button>
    </div>
    <div v-if="showSummary" class="text-sm text-gray-600">
      Showing
      <span class="font-semibold text-gray-900 tabular-nums">
        {{ startIndex + 1 }}
      </span>
      to
      <span class="font-semibold text-gray-900 tabular-nums">
        {{ endIndex }}
      </span>
      of
      <span class="font-semibold text-gray-900 tabular-nums">
        {{ totalItems }}
      </span>
      results
    </div>
  </nav>
</template>

<script setup>
import { computed, onMounted, ref } from "vue";
import { Icon } from "@iconify/vue";

const props = defineProps({
  currentPage: {
    type: Number,
    required: true,
  },
  totalPages: {
    type: Number,
    required: true,
  },
  totalItems: {
    type: Number,
    required: true,
  },
  itemsPerPage: {
    type: Number,
    required: true,
  },
  label: {
    type: String,
    default: "Pagination",
  },
  showSummary: {
    type: Boolean,
    default: true,
  },
});

const emit = defineEmits(["first", "previous", "page", "next", "last"]);

const isFirstPage = computed(() => props.currentPage === 1);
const isLastPage = computed(() => props.currentPage === props.totalPages);

const startIndex = computed(() => (props.currentPage - 1) * props.itemsPerPage);
const endIndex = computed(() =>
  Math.min(props.currentPage * props.itemsPerPage, props.totalItems)
);

const isSmallScreen = ref(false);

onMounted(() => {
  const media = window.matchMedia("(max-width: 639px)");
  console.log("media", media);
  // Set initial value
  isSmallScreen.value = media.matches;

  // Listen for changes
  const listener = (e) => {
    isSmallScreen.value = e.matches;
  };

  media.addEventListener("change", listener);

  // Optional: cleanup
  onUnmounted(() => media.removeEventListener("change", listener));
});

// Windowed page numbers with ellipses, e.g. 1 … 4 5 6 … 9
const DOTS = "...";

function range(start, end) {
  const length = end - start + 1;
  return Array.from({ length }, (_, idx) => idx + start);
}

// Fewer sibling buttons on mobile
const siblingCount = computed(() => (isSmallScreen.value ? 0 : 1));

const paginationRange = computed(() => {
  const total = props.totalPages;
  const current = props.currentPage;
  const siblings = siblingCount.value;

  // first + last + current + 2*siblings + 2*DOTS
  const totalPageNumbers = siblings * 2 + 5;

  if (total <= totalPageNumbers) {
    return range(1, total);
  }

  const leftSiblingIndex = Math.max(current - siblings, 1);
  const rightSiblingIndex = Math.min(current + siblings, total);

  const shouldShowLeftDots = leftSiblingIndex > 2;
  const shouldShowRightDots = rightSiblingIndex < total - 2;

  if (!shouldShowLeftDots && shouldShowRightDots) {
    const leftItemCount = 3 + siblings * 2;
    return [...range(1, leftItemCount), DOTS, total];
  }

  if (shouldShowLeftDots && !shouldShowRightDots) {
    const rightItemCount = 3 + siblings * 2;
    return [1, DOTS, ...range(total - rightItemCount + 1, total)];
  }

  return [1, DOTS, ...range(leftSiblingIndex, rightSiblingIndex), DOTS, total];
});
</script>
